/**
 * Providers & failover routing.
 *
 * Each capability (payout, collection, biller) carries an ordered `priority` list and a
 * `disabled` set. The failover wrapper tries providers in priority order, skipping any
 * that are disabled — so the order on this page *is* the failover policy. Reordering and
 * enabling/disabling auto-save (optimistically) and are gated on `providers:write`
 * (finance, superadmin).
 *
 * Three things this page has to make impossible to get wrong, because more than one
 * vendor can serve every one of these rails and two of them serving at once is the
 * failure mode worth designing against.
 *
 * **Who is actually first.** Not "whatever is at the top of the list" — the API sends a
 * per-entry `primary` flag computed the same way the router computes it: first ranked,
 * not disabled, *and* live. The third condition is the one that catches people out. An
 * operator ranks Paystack first, never finishes pasting its keys, and the rail is quietly
 * served by Flutterwave while every screen they have says Paystack. So a ranked vendor
 * with no credentials is drawn struck-through with "no credentials", and the Primary
 * badge goes where the money will actually go.
 *
 * **Vendors nobody ranked.** A vendor whose credentials are live but which no operator
 * placed in the order is listed below the ranked ones, greyed, with an "Add to order"
 * button — and it is *not* routed to until somebody presses it. The API refuses to append
 * it silently, and this page exists so that refusal does not turn into an invisible
 * vendor: Accelerate going live for bills must not put payouts on Accelerate, but it also
 * must not mean nobody ever discovers Accelerate could take payouts.
 *
 * **Defaults are not choices.** A capability with no `provider_configs` row is running on
 * the environment's fallback order. That is shown as such — "environment default" — with
 * a button to adopt it, because the state where nobody has chosen is exactly the state
 * where a newly-credentialed second rail sits idle forever.
 */
import { useState } from 'react';

import { messageOf } from '../api/errors.js';
import { useAuth } from '../auth/AuthContext.js';
import { adminCan } from '../contracts/permissions.js';
import type { ProviderConfig } from '../contracts/types.js';
import { Badge, Button, Card, ErrorBox, LoadingState, Switch } from '../components/ui.js';
import { IconAlert, IconChevronDown, IconChevronUp } from '../components/icons.js';
import { useToast } from '../components/Toast.js';
import { useAsync } from '../hooks/useAsync.js';
import { humanize, relativeTime } from '../lib/format.js';

const CAPABILITY_COPY: Record<string, string> = {
  payout: 'Sending naira out: bank transfers and withdrawals.',
  collection: 'Taking money in: card top-ups and virtual-account funding.',
  biller: 'Airtime, data, electricity and TV top-ups.',
};

/**
 * The annotated row the API sends per vendor.
 *
 * Derived from `ProviderConfig` rather than imported, because `entries` is optional on the
 * contract: the console and the API deploy separately, so a build of this page can be
 * talking to an API that predates the field. Everything below therefore has a path for
 * "the API did not send entries", which reconstructs the old view from `priority` and
 * `disabled` alone — minus the liveness it cannot know.
 */
type Entry = NonNullable<ProviderConfig['entries']>[number];

/**
 * Recompute the annotated rows for a given order, reusing whatever the API last told us
 * about each vendor.
 *
 * Two callers, one rule. It is the fallback when the API is older than this build and sent
 * no `entries` at all (in which case `known` is empty and liveness is assumed, because a
 * page that greyed out every vendor on an older API would be lying in the other
 * direction). And it is what redraws the card during the round-trip after a reorder, so
 * the ranks and the Primary badge move with the click instead of lagging a request behind
 * — the alternative being a page that briefly shows a precedence nobody is running.
 */
function entriesFor(priority: string[], disabled: string[], known: readonly Entry[]): Entry[] {
  const off = new Set(disabled);
  const by = new Map(known.map((entry) => [entry.slug, entry]));
  const rowFor = (slug: string, rank: number | null): Entry => {
    const prior = by.get(slug);
    return {
      slug,
      rank,
      ranked: rank !== null,
      disabled: off.has(slug),
      live: prior?.live ?? true,
      primary: false,
      label: prior?.label ?? slug,
    };
  };

  const ranked = priority.map((slug, index) => rowFor(slug, index + 1));
  // Primary is "first ranked, enabled and live" — the same three conditions the router
  // applies, in the same order. Anything else on this page would be a second definition of
  // precedence, which is the thing the whole feature exists to abolish.
  const first = ranked.find((entry) => !entry.disabled && entry.live);
  if (first !== undefined) first.primary = true;

  const rest = [...known.map((entry) => entry.slug), ...disabled].filter(
    (slug) => !priority.includes(slug),
  );
  const seen = new Set<string>();
  const unranked = rest.filter((slug) => (seen.has(slug) ? false : (seen.add(slug), true)));

  return [...ranked, ...unranked.map((slug) => rowFor(slug, null))];
}

export function ProvidersPage(): JSX.Element {
  const { api, session } = useAuth();
  const toast = useToast();
  const canWrite = session !== null && adminCan(session.me.role, 'providers:write');
  const state = useAsync(() => api.listProviders(), []);
  const [busy, setBusy] = useState<string | null>(null);

  async function save(cfg: ProviderConfig, priority: string[], disabled: string[], note: string) {
    setBusy(cfg.capability);
    state.setData((prev) =>
      prev === null
        ? prev
        : {
            providers: prev.providers.map((p) => {
              if (p.capability !== cfg.capability) return p;
              const entries = entriesFor(priority, disabled, p.entries ?? []);
              return {
                ...p,
                priority,
                disabled,
                entries,
                primary: entries.find((entry) => entry.primary)?.slug ?? null,
              };
            }),
          },
    );
    try {
      const updated = await api.setProvider(cfg.capability, { priority, disabled });
      state.setData((prev) =>
        prev === null
          ? prev
          : { providers: prev.providers.map((p) => (p.capability === cfg.capability ? updated : p)) },
      );
      toast.success('Routing updated', note);
    } catch (err) {
      // Reload rather than roll back by hand: the response carries the re-resolved plan
      // (who is primary now, who is live), and a failed write leaves the optimistic edit
      // describing a precedence that is not in force. Guessing is the one thing this page
      // must not do.
      state.reload();
      toast.error('Could not update routing', messageOf(err));
    } finally {
      setBusy(null);
    }
  }

  if (state.loading) return <LoadingState label="Loading provider routing…" />;
  if (state.error !== null) return <ErrorBox message={state.error} />;
  if (state.data === null) return <ErrorBox message="No provider data." />;

  return (
    <>
      <div className="page-head">
        <h1 className="page-head__title">Providers &amp; failover</h1>
        <p className="page-head__sub">
          Ordering drives failover: each rail tries its providers top-to-bottom and skips any that
          are disabled or have no credentials. Exactly one provider serves a rail at a time, the
          one marked Primary. Move a healthy provider up to prefer it; disable one to route around
          an outage.{!canWrite && ' Your role can view routing but not change it.'}
        </p>
      </div>

      <div className="grid grid--2">
        {state.data.providers.map((cfg) => (
          <ProviderCard
            key={cfg.capability}
            cfg={cfg}
            canWrite={canWrite}
            busy={busy === cfg.capability}
            onSave={save}
          />
        ))}
      </div>
    </>
  );
}

function ProviderCard({
  cfg,
  canWrite,
  busy,
  onSave,
}: {
  cfg: ProviderConfig;
  canWrite: boolean;
  busy: boolean;
  onSave: (cfg: ProviderConfig, priority: string[], disabled: string[], note: string) => void;
}): JSX.Element {
  const entries = cfg.entries ?? entriesFor(cfg.priority, cfg.disabled, []);
  const ranked = entries.filter((entry) => entry.ranked);
  const unranked = entries.filter((entry) => !entry.ranked);
  const primary = entries.find((entry) => entry.primary) ?? null;
  const stored = cfg.stored !== false;

  function move(slug: string, dir: -1 | 1) {
    const idx = cfg.priority.indexOf(slug);
    const target = idx + dir;
    if (idx === -1 || target < 0 || target >= cfg.priority.length) return;
    const next = [...cfg.priority];
    const [item] = next.splice(idx, 1);
    next.splice(target, 0, item!);
    onSave(cfg, next, cfg.disabled, `${humanize(slug)} moved ${dir === -1 ? 'up' : 'down'}.`);
  }

  function setDisabled(slug: string, disable: boolean) {
    const next = disable
      ? [...cfg.disabled, slug]
      : cfg.disabled.filter((s) => s !== slug);
    onSave(cfg, cfg.priority, next, `${humanize(slug)} ${disable ? 'disabled' : 'enabled'}.`);
  }

  /**
   * Rank an unranked vendor — at the *bottom*, never the top.
   *
   * Adding a rail is a decision to have it as a fallback; making it primary is a separate
   * decision, taken with the arrows, one row at a time. Landing a newly-added vendor at
   * the top would mean a single click silently moved every payout onto a rail that has
   * never carried one.
   */
  function addToOrder(slug: string) {
    if (cfg.priority.includes(slug)) return;
    onSave(
      cfg,
      [...cfg.priority, slug],
      cfg.disabled,
      `${humanize(slug)} added as the last fallback. Move it up to prefer it.`,
    );
  }

  /** Write the environment default down as this deployment's own choice. */
  function adoptDefault() {
    onSave(cfg, cfg.priority, cfg.disabled, 'Default order saved as this deployment’s routing.');
  }

  return (
    <Card
      title={
        <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {humanize(cfg.capability)}
          <Badge tone={primary === null ? 'red' : 'green'}>
            {primary === null ? 'nobody serving' : `via ${primary.label}`}
          </Badge>
        </span>
      }
      actions={
        <span className="faint" style={{ fontSize: 12 }}>
          {cfg.updatedAt === null ? 'never set' : `updated ${relativeTime(cfg.updatedAt)}`}
        </span>
      }
    >
      <p className="muted" style={{ marginTop: -4 }}>
        {CAPABILITY_COPY[cfg.capability] ?? 'Provider routing.'}
      </p>

      {primary === null && (
        <div className="error-box" style={{ marginTop: 12 }}>
          <IconAlert size={16} />
          <div>
            Nothing can handle this. Every option is either switched off or missing its keys, so customers will see an error.
          </div>
        </div>
      )}

      {!stored && (
        <div className="error-box" style={{ marginTop: 12 }}>
          <IconAlert size={16} />
          <div>
            This order is the <strong>environment default</strong>, not a choice anyone made here.
            Save it to take ownership. Until then a newly-enabled vendor will not be routed to.
            {canWrite && (
              <div style={{ marginTop: 8 }}>
                <Button size="sm" variant="primary" disabled={busy} onClick={adoptDefault}>
                  Save this order
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="prov-list">
        {ranked.map((entry) => (
          <div
            key={entry.slug}
            className={`prov-item${entry.disabled || !entry.live ? ' prov-item--disabled' : ''}`}
          >
            <span className="prov-item__rank">{entry.rank}</span>
            <div className="prov-move">
              <button
                aria-label={`Move ${entry.slug} up`}
                disabled={!canWrite || busy || entry.rank === null || entry.rank <= 1}
                onClick={() => move(entry.slug, -1)}
              >
                <IconChevronUp size={11} />
              </button>
              <button
                aria-label={`Move ${entry.slug} down`}
                disabled={!canWrite || busy || entry.rank === null || entry.rank >= ranked.length}
                onClick={() => move(entry.slug, 1)}
              >
                <IconChevronDown size={11} />
              </button>
            </div>
            <span className="prov-item__name">{entry.label}</span>
            {entry.primary && <Badge tone="violet">Primary</Badge>}
            {/* Ranked, switched on, and still never called. The single most misleading
                state this page can be in, so it is named rather than merely greyed. */}
            {!entry.live && !entry.disabled && <Badge tone="amber">No credentials</Badge>}
            <span className="prov-item__spacer" />
            <span className="faint" style={{ fontSize: 12 }}>
              {entry.disabled ? 'Disabled' : 'Enabled'}
            </span>
            <Switch
              checked={!entry.disabled}
              disabled={!canWrite || busy}
              label={`${entry.disabled ? 'Enable' : 'Disable'} ${entry.slug}`}
              onChange={(on) => setDisabled(entry.slug, !on)}
            />
          </div>
        ))}
      </div>

      {unranked.length > 0 && (
        <>
          <p className="muted" style={{ marginTop: 14, marginBottom: 6, fontSize: 12 }}>
            Not in use. These can handle it, but are never tried until you add them to the list above.
          </p>
          <div className="prov-list">
            {unranked.map((entry) => (
              <div key={entry.slug} className="prov-item prov-item--disabled">
                <span className="prov-item__rank">–</span>
                <span className="prov-item__name">{entry.label}</span>
                {entry.disabled ? (
                  <Badge tone="neutral">Disabled</Badge>
                ) : entry.live ? (
                  <Badge tone="blue">Live, unranked</Badge>
                ) : (
                  <Badge tone="neutral">No credentials</Badge>
                )}
                <span className="prov-item__spacer" />
                {canWrite && (
                  <Button size="sm" disabled={busy} onClick={() => addToOrder(entry.slug)}>
                    Add to order
                  </Button>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </Card>
  );
}
